import CONFIG from '../../../../config';
import { buildHybridQueryPlan } from '../../aiQueryHelpers';
import { executeRefineStage, executeSufficiencyStage } from '../sufficiency';
import { createStageResult, createRetrievePayload } from '../contracts';
import { buildContextKeywordCoverage, analyzeImplicitTargetPrompt } from './audit';

const CONTEXT_REFINE_MAX_EXTRA_QUERIES = 2;
const EXHAUSTIVE_SCOPE_MIN_ITEMS = 10;
const REFINE_QUERY_STOPWORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'into', 'of', 'on', 'or', 'the', 'to', 'toward', 'towards', 'with'
]);

const isUsefulRefineToken = (value = '') => {
  const token = String(value || '').trim().toLowerCase();
  if (!token) return false;
  if (token.length < 4) return false;
  if (REFINE_QUERY_STOPWORDS.has(token)) return false;
  if (/^\d+$/.test(token)) return false;
  return true;
};

const isUsefulHotDocName = (value = '') => {
  const docName = String(value || '').trim();
  if (!docName) return false;
  if (docName.length < 3) return false;
  if (!/[a-z]{2,}/i.test(docName)) return false;
  return true;
};

// Detects prompts that imply exhaustive scope for stricter sufficiency expectations.
const hasExhaustiveScopeCue = (value = '') => {
  const text = String(value || '').toLowerCase();
  if (!text) return false;
  return /\ball\b|\bevery\b|\beach\b|\bcomplete\b|\bentire\b|\bfull\b|\bexhaustive\b/i.test(text);
};

const hasDefinitionPromptCue = (value = '') => {
  const text = String(value || '').toLowerCase().trim();
  if (!text) return false;
  return /^(what\s+is|what\s+are|define|definition\s+of)\b/i.test(text);
};

// Evaluates retrieved context sufficiency with keyword coverage and scope-aware thresholds.
export const evaluateContextSufficiency = ({
  userText = '',
  items = [],
  retrievalMeta = null,
  intentKey = 'ask',
  minItems = 3,
  minChars = 700,
  minCoverageRatio = 0.34,
  minMatchedKeywords = 2
} = {}) => {
  const selectedItems = Array.isArray(items) ? items : [];
  const selectedCount = selectedItems.length;
  const selectedChars = selectedItems.reduce((total, item) => {
    return total + String(item?.text || '').trim().length;
  }, 0);
  const retrievalReasons = Array.isArray(retrievalMeta?.retrievalReasons)
    ? retrievalMeta.retrievalReasons
    : [];
  const implicitTargetSignals = analyzeImplicitTargetPrompt({ userText });
  const assumeAttachmentDefaultTarget = retrievalReasons.includes('attachment_default_target_assumed')
    && implicitTargetSignals.shouldAssumeAttachmentTarget;
  const keywordCoverage = assumeAttachmentDefaultTarget
    ? {
      userKeywords: [],
      matchedKeywords: [],
      missingKeywords: [],
      coverageRatio: 1,
      implicitTarget: 'attachment_default'
    }
    : buildContextKeywordCoverage({ userText, items: selectedItems });
  const userKeywordCount = Array.isArray(keywordCoverage?.userKeywords)
    ? keywordCoverage.userKeywords.length
    : 0;
  const matchedKeywordCount = Array.isArray(keywordCoverage?.matchedKeywords)
    ? keywordCoverage.matchedKeywords.length
    : 0;
  const missingKeywordCount = Array.isArray(keywordCoverage?.missingKeywords)
    ? keywordCoverage.missingKeywords.length
    : 0;
  const reasons = [];

  const requiredItems = Math.max(1, Number(minItems || 3));
  const requiredChars = Math.max(120, Number(minChars || 700));
  const requiredCoverage = Math.max(0, Math.min(1, Number(minCoverageRatio || 0.34)));
  const configuredMatchedKeywords = Math.max(1, Number(minMatchedKeywords || 2));
  const requiredMatchedKeywords = Math.max(
    1,
    Math.min(userKeywordCount > 0 ? userKeywordCount : configuredMatchedKeywords, configuredMatchedKeywords)
  );

  if (selectedCount < requiredItems) reasons.push('selected_items_below_min');
  if (selectedChars < requiredChars) reasons.push('selected_chars_below_min');
  if (Number(keywordCoverage?.coverageRatio || 0) < requiredCoverage) reasons.push('keyword_coverage_below_min');
  if (matchedKeywordCount < requiredMatchedKeywords) reasons.push('matched_keywords_below_min');

  const selectedDocBreakdown = Array.isArray(retrievalMeta?.selectedDocBreakdown)
    ? retrievalMeta.selectedDocBreakdown
    : [];
  const topDoc = selectedDocBreakdown[0] || null;
  const topDocShare = selectedCount > 0 && topDoc
    ? Number(topDoc.count || 0) / selectedCount
    : 0;
  const definitionPrompt = hasDefinitionPromptCue(userText);
  const exhaustiveRequested = hasExhaustiveScopeCue(userText)
    || String(intentKey || '').trim().toLowerCase() === 'summarize';

  // If we still miss user terms while context is narrow or concentrated,
  // force one refine loop to avoid generic answers over thin evidence.
  if (
    missingKeywordCount > 0
    && (
      selectedCount <= Math.max(requiredItems * 2, 6)
      || topDocShare >= 0.9
    )
  ) {
    reasons.push('keyword_coverage_incomplete_narrow_context');
  }

  if (exhaustiveRequested) {
    if (selectedCount < Math.max(requiredItems, EXHAUSTIVE_SCOPE_MIN_ITEMS)) {
      reasons.push('exhaustive_scope_low_item_coverage');
    }
    if (retrievalReasons.includes('budget-truncation')) {
      reasons.push('exhaustive_scope_budget_truncated');
    }
    if (topDocShare >= 0.5 && retrievalReasons.includes('budget-truncation')) {
      reasons.push('hot_doc_expansion_required');
    }
  }

  const targetDocDiscoveryRequested = retrievalReasons.includes('hot_doc_name_discovery_probe');
  const targetDocMatchedInSelection = retrievalReasons.includes('hot_doc_name_match')
    || selectedItems.some((item) => Boolean(item?.meta?.docNameMatched || item?.meta?.docNameCueMatched));
  if (targetDocDiscoveryRequested && !targetDocMatchedInSelection) {
    if (retrievalReasons.includes('budget-truncation') || topDocShare >= 0.85) {
      reasons.push('target_doc_not_selected');
    }
  }

  const hasDefinitionAnchorReason = retrievalReasons.some((reason) => (
    reason === 'hot_header_match'
    || reason === 'linked_header_trailing_expansion'
    || reason === 'linked_section_trailing_expansion'
    || reason === 'section_scope_detected'
    || reason === 'hot_doc_name_match'
  ));
  const hasDefinitionAnchorTrigger = selectedItems.some((item) => {
    const trigger = String(item?.meta?.trigger || '').trim().toLowerCase();
    return trigger.includes('header') || trigger.includes('section');
  });
  if (
    definitionPrompt
    && userKeywordCount <= 2
    && topDocShare >= 0.85
    && matchedKeywordCount <= 1
    && !hasDefinitionAnchorReason
    && !hasDefinitionAnchorTrigger
  ) {
    reasons.push('definition_prompt_anchor_missing');
  }

  const noKeywordSignals = !Array.isArray(keywordCoverage?.userKeywords) || keywordCoverage.userKeywords.length === 0;
  const hasMinimumVolume = selectedCount >= requiredItems || selectedChars >= requiredChars;
  const isSufficient = noKeywordSignals
    ? hasMinimumVolume
    : reasons.length === 0;

  const coverageScore = Math.max(0, Math.min(1, Number(keywordCoverage?.coverageRatio || 0)));
  const itemScore = Math.max(0, Math.min(1, selectedCount / requiredItems));
  const charScore = Math.max(0, Math.min(1, selectedChars / requiredChars));
  const score = Number((coverageScore * 0.55 + itemScore * 0.25 + charScore * 0.2).toFixed(3));

  return {
    isSufficient,
    decisionCode: isSufficient ? 'sufficient' : 'insufficient',
    reasonCodes: isSufficient ? [] : Array.from(new Set(reasons)),
    exhaustiveRequested,
    selectedCount,
    selectedChars,
    keywordCoverage,
    topDoc,
    topDocShare: Number(topDocShare.toFixed(3)),
    matchedKeywordCount,
    thresholds: {
      minItems: requiredItems,
      minChars: requiredChars,
      minCoverageRatio: requiredCoverage,
      minMatchedKeywords: requiredMatchedKeywords
    },
    score
  };
};

// Builds a refined query plan when sufficiency indicates context gaps.
export const buildRefineQueryPlan = ({ queryPlan = null, sufficiency = null, userText = '' } = {}) => {
  const baseQueries = Array.isArray(queryPlan?.queries) ? queryPlan.queries : [];
  const maxExtraQueries = Math.max(
    1,
    Number(CONFIG.AI?.CHAT_ORCHESTRATION?.REFINE_MAX_EXTRA_QUERIES || CONTEXT_REFINE_MAX_EXTRA_QUERIES)
  );
  const missingKeywords = Array.isArray(sufficiency?.keywordCoverage?.missingKeywords)
    ? sufficiency.keywordCoverage.missingKeywords
    : [];
  const extraQueries = missingKeywords
    .slice(0, maxExtraQueries)
    .map((token) => String(token || '').trim())
    .filter(isUsefulRefineToken);
  if (extraQueries.length === 0 && String(userText || '').trim()) {
    extraQueries.push(`${String(userText || '').trim()} details`);
  }

  const hotDocName = String(sufficiency?.topDoc?.docName || '').trim();
  if (isUsefulHotDocName(hotDocName)) {
    extraQueries.push(`${hotDocName} detailed evidence examples`);
  }

  const mergedQueries = Array.from(new Set([
    ...baseQueries,
    ...extraQueries
  ])).filter(Boolean);

  const baseBudget = queryPlan?.retrievalBudget && typeof queryPlan.retrievalBudget === 'object'
    ? queryPlan.retrievalBudget
    : {};
  const itemBoost = Math.max(1, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.REFINE_BUDGET_ITEM_BOOST || 4));
  const charBoost = Math.max(120, Number(CONFIG.AI?.CHAT_ORCHESTRATION?.REFINE_BUDGET_CHAR_BOOST || 3000));
  const projectResultsBoost = Math.max(
    1,
    Number(CONFIG.AI?.CHAT_ORCHESTRATION?.REFINE_BUDGET_PROJECT_RESULTS_BOOST || 8)
  );

  return {
    ...queryPlan,
    source: 'phase-b-hybrid-planner-refine',
    queries: mergedQueries,
    retrievalBudget: {
      ...baseBudget,
      maxContextItems: Math.min(28, Math.max(1, Number(baseBudget.maxContextItems || 14) + itemBoost)),
      maxContextChars: Math.min(22000, Math.max(120, Number(baseBudget.maxContextChars || 12000) + charBoost)),
      maxProjectResults: Math.min(48, Math.max(1, Number(baseBudget.maxProjectResults || 24) + projectResultsBoost))
    },
    refine: {
      applied: true,
      source: 'sufficiency-loop',
      addedQueries: extraQueries
    }
  };
};

// Builds a retrieval query plan for the current turn and returns plan stage payload.
export const executePlanStage = ({
  userText = '',
  intent = null,
  contextPlan = null,
  buildQueryPlan = buildHybridQueryPlan
} = {}) => {
  const queryPlan = buildQueryPlan({
    userText,
    intent,
    contextPlan: {
      activeDocId: contextPlan?.activeDocId,
      linkedDocIds: contextPlan?.linkedDocIds
    }
  });

  return {
    queryPlan,
    planResult: createStageResult({
      stage: 'plan',
      status: 'ok',
      decisionCode: 'plan_built',
      payload: {
        planSource: queryPlan?.source || null,
        queryCount: Array.isArray(queryPlan?.queries) ? queryPlan.queries.length : 0,
        queries: Array.isArray(queryPlan?.queries) ? queryPlan.queries : [],
        maxContextItems: Number(queryPlan?.retrievalBudget?.maxContextItems || 0),
        maxContextChars: Number(queryPlan?.retrievalBudget?.maxContextChars || 0),
        maxProjectResults: Number(queryPlan?.retrievalBudget?.maxProjectResults || 0),
        disambiguation: queryPlan?.disambiguation || null
      }
    })
  };
};

// Runs plan stage and emits plan telemetry payload in one call.
export const executePlanAndEmitStage = ({
  userText = '',
  intent = null,
  contextPlan = null,
  emitStage,
  executePlanStageFn = executePlanStage,
  buildQueryPlan = buildHybridQueryPlan
} = {}) => {
  const { queryPlan, planResult } = executePlanStageFn({
    userText,
    intent,
    contextPlan,
    buildQueryPlan
  });

  if (typeof emitStage === 'function') {
    emitStage('plan', {
      ...(planResult?.payload || {}),
      disambiguation: queryPlan?.disambiguation || null
    });
  }

  return {
    queryPlan,
    planResult
  };
};

// Executes retrieval pipeline for one iteration and returns normalized retrieve stage payload.
export const executeRetrieveStage = async ({
  docJson,
  userText = '',
  queryPlan,
  contextPlan,
  routerContract = null,
  retrievalIteration = 1,
  signal,
  searchProject,
  loadLinkedDocContent,
  loadLinkedDocChunks,
  retrievePipeline
} = {}) => {
  if (typeof retrievePipeline !== 'function') {
    throw new Error('executeRetrieveStage requires retrievePipeline function');
  }

  const retrieval = await retrievePipeline({
    docJson,
    userText,
    queryPlan,
    contextPlan,
    routerContract,
    signal,
    searchProject,
    loadLinkedDocContent,
    loadLinkedDocChunks
  });

  const selectedChunkIds = Array.from(new Set((Array.isArray(retrieval?.items) ? retrieval.items : [])
    .map((item) => String(item?.meta?.chunkHash || '').trim())
    .filter(Boolean)));

  return {
    retrieval,
    retrieveResult: createStageResult({
      stage: 'retrieve',
      status: 'ok',
      decisionCode: Array.isArray(retrieval?.items) && retrieval.items.length > 0
        ? 'context_ready'
        : 'context_empty',
      payload: createRetrievePayload({
        retrievalIteration,
        selectedCount: Array.isArray(retrieval?.items) ? retrieval.items.length : 0,
        selectedChars: Number(retrieval?.meta?.selectedChars || 0),
        candidateCount: Number(retrieval?.meta?.totalCandidates || 0),
        maxContextItems: Number(queryPlan?.retrievalBudget?.maxContextItems || 0),
        maxContextChars: Number(queryPlan?.retrievalBudget?.maxContextChars || 0),
        maxProjectResults: Number(queryPlan?.retrievalBudget?.maxProjectResults || 0),
        maxLinkedDocs: Number(queryPlan?.retrievalBudget?.maxLinkedDocs || 0),
        historyRecentQaPairs: Number(queryPlan?.retrievalBudget?.historyRecentQaPairs || 0),
        recencyWeight: Number(contextPlan?.recencyWeight || 0),
        recencyDecayTurns: Number(contextPlan?.recencyDecayTurns || 0),
        includeActiveDoc: Boolean(contextPlan?.includeActiveDoc),
        includeLinkedDocs: Boolean(contextPlan?.includeLinkedDocs),
        includeChatHistory: Boolean(contextPlan?.includeChatHistory),
        includeProjectSearch: Boolean(contextPlan?.includeProjectSearch),
        templateDocId: Number(contextPlan?.templateDocId || 0) || null,
        linkedDocIds: Array.isArray(contextPlan?.linkedDocIds) ? contextPlan.linkedDocIds : [],
        isSingleDocContext: Boolean(retrieval?.meta?.isSingleDocContext),
        sourceBreakdown: retrieval?.meta?.sourceBreakdown || {},
        selectedChunkIds,
        fetchedChunkIds: Array.isArray(retrieval?.meta?.fetchedChunkIds) ? retrieval.meta.fetchedChunkIds : [],
        retrievalReasons: Array.isArray(retrieval?.meta?.retrievalReasons) ? retrieval.meta.retrievalReasons : [],
        selectedSourceBreakdown: retrieval?.meta?.selectedSourceBreakdown || {},
        selectedDocBreakdown: Array.isArray(retrieval?.meta?.selectedDocBreakdown) ? retrieval.meta.selectedDocBreakdown : [],
        selectionAudit: retrieval?.meta?.selectionAudit || null,
        contextKeywordCoverage: retrieval?.meta?.contextKeywordCoverage || null,
        projectSearchQueryCount: Number(retrieval?.meta?.projectSearchQueryCount || 0),
        projectSearchPlannedQueries: Array.isArray(retrieval?.meta?.projectSearchPlannedQueries)
          ? retrieval.meta.projectSearchPlannedQueries
          : [],
        projectSearchAttemptedQueries: Array.isArray(retrieval?.meta?.projectSearchAttemptedQueries)
          ? retrieval.meta.projectSearchAttemptedQueries
          : [],
        projectSearchErrors: Array.isArray(retrieval?.meta?.projectSearchErrors)
          ? retrieval.meta.projectSearchErrors
          : []
      })
    })
  };
};

// Runs retrieve, sufficiency, and optional refine-retrieve loops as a single stage sub-orchestration.
export const executeRetrieveSufficiencyFlow = async ({
  runtimeEnabled = false,
  legacyFallback = false,
  docJson,
  userText = '',
  queryPlan,
  contextPlan,
  routerContract = null,
  intent,
  signal,
  emitStage,
  onWarn,
  retrievePipeline,
  executeRetrieveStageFn = executeRetrieveStage,
  executeSufficiencyStageFn = executeSufficiencyStage,
  executeRefineStageFn = executeRefineStage,
  evaluateSufficiency,
  buildRefinePlan,
  summarizeContextReferences,
  summarizeContextItems,
  buildKeywordCoverage,
  sufficiencyConfig = {},
  sufficiencyEnabled = true,
  refineEnabled = true,
  maxRefineLoops = 1
} = {}) => {
  let nextQueryPlan = queryPlan;
  let contextRetrieval = null;

  if (runtimeEnabled) {
    try {
      const retrieveStage = await executeRetrieveStageFn({
        docJson,
        userText,
        queryPlan: nextQueryPlan,
        contextPlan,
        routerContract,
        retrievalIteration: 1,
        signal,
        retrievePipeline
      });
      contextRetrieval = retrieveStage.retrieval;
      if (typeof emitStage === 'function') {
        emitStage('retrieve', {
          ...retrieveStage.retrieveResult.payload,
          contextReferences: typeof summarizeContextReferences === 'function'
            ? summarizeContextReferences(contextRetrieval?.items, 6)
            : [],
          selectedContextItems: typeof summarizeContextItems === 'function'
            ? summarizeContextItems(contextRetrieval?.items, 8)
            : [],
          contextKeywordCoverage: typeof buildKeywordCoverage === 'function'
            ? buildKeywordCoverage({ userText, items: contextRetrieval?.items })
            : null,
          retrievalDecision: retrieveStage.retrieveResult.decisionCode
        });
      }
    } catch (error) {
      if (typeof onWarn === 'function') {
        onWarn(error?.message || 'context retrieval pipeline failed');
      }
      if (!legacyFallback) {
        throw error;
      }
      contextRetrieval = null;
    }
  } else if (typeof emitStage === 'function') {
    emitStage('retrieve', {
      retrievalIteration: 1,
      selectedCount: 0,
      selectedChars: 0,
      candidateCount: 0,
      retrievalDecision: 'context_empty'
    });
  }

  let retrievalIterationCount = 1;
  if (typeof executeSufficiencyStageFn !== 'function') {
    throw new Error('executeRetrieveSufficiencyFlow requires executeSufficiencyStageFn');
  }

  let { sufficiency: contextSufficiency, sufficiencyResult } = executeSufficiencyStageFn({
    userText,
    intent,
    retrieval: contextRetrieval,
    config: sufficiencyConfig,
    evaluateSufficiency
  });

  if (!sufficiencyEnabled) {
    contextSufficiency = {
      ...contextSufficiency,
      isSufficient: true,
      decisionCode: 'sufficiency_disabled',
      reasonCodes: []
    };
    sufficiencyResult = {
      ...sufficiencyResult,
      status: 'sufficient',
      decisionCode: 'sufficiency_disabled',
      reasonCodes: [],
      payload: {
        ...sufficiencyResult.payload,
        sufficiencyScore: Number(contextSufficiency.score || 0)
      }
    };
  }

  if (typeof emitStage === 'function') {
    emitStage('sufficiency', {
      retrievalIteration: retrievalIterationCount,
      status: sufficiencyResult?.status || (contextSufficiency.isSufficient ? 'sufficient' : 'insufficient'),
      decisionCode: sufficiencyResult?.decisionCode || contextSufficiency.decisionCode,
      reasonCodes: sufficiencyResult?.reasonCodes || contextSufficiency.reasonCodes,
      ...sufficiencyResult?.payload
    });
  }

  if (runtimeEnabled && refineEnabled && maxRefineLoops > 0 && !contextSufficiency.isSufficient) {
    if (typeof executeRefineStageFn !== 'function') {
      throw new Error('executeRetrieveSufficiencyFlow requires executeRefineStageFn when refine is enabled');
    }
    const { refinedQueryPlan, refineResult: refinePlanResult } = executeRefineStageFn({
      queryPlan: nextQueryPlan,
      sufficiency: contextSufficiency,
      userText,
      buildRefinePlan
    });

    try {
      const refinedRetrieveStage = await executeRetrieveStageFn({
        docJson,
        userText,
        queryPlan: refinedQueryPlan,
        contextPlan,
        routerContract,
        retrievalIteration: retrievalIterationCount + 1,
        signal,
        retrievePipeline
      });
      const refinedRetrieval = refinedRetrieveStage.retrieval;
      retrievalIterationCount += 1;

      const { sufficiency: refinedSufficiency, sufficiencyResult: refinedSufficiencyResult } = executeSufficiencyStageFn({
        userText,
        intent,
        retrieval: refinedRetrieval,
        config: sufficiencyConfig,
        evaluateSufficiency
      });

      const shouldAdoptRefined = refinedSufficiency.score > contextSufficiency.score
        || (
          refinedSufficiency.score === contextSufficiency.score
          && Number(refinedSufficiency.selectedChars || 0) > Number(contextSufficiency.selectedChars || 0)
        );

      if (typeof emitStage === 'function') {
        emitStage('refine', {
          status: 'completed',
          retrievalIteration: retrievalIterationCount,
          decisionCode: shouldAdoptRefined ? 'refine_adopted' : 'refine_retained_original',
          reasonCodes: shouldAdoptRefined ? ['sufficiency_improved'] : ['sufficiency_not_improved'],
          addedQueries: Array.isArray(refinePlanResult?.payload?.addedQueries)
            ? refinePlanResult.payload.addedQueries
            : (Array.isArray(refinedQueryPlan?.refine?.addedQueries) ? refinedQueryPlan.refine.addedQueries : []),
          before: {
            selectedCount: contextSufficiency.selectedCount,
            selectedChars: contextSufficiency.selectedChars,
            score: contextSufficiency.score,
            keywordCoverage: contextSufficiency.keywordCoverage
          },
          after: {
            selectedCount: refinedSufficiency.selectedCount,
            selectedChars: refinedSufficiency.selectedChars,
            score: refinedSufficiency.score,
            keywordCoverage: refinedSufficiency.keywordCoverage,
            reasonCodes: refinedSufficiency.reasonCodes
          }
        });
      }

      if (shouldAdoptRefined) {
        nextQueryPlan = refinedQueryPlan;
        contextRetrieval = refinedRetrieval;
        contextSufficiency = refinedSufficiency;

        if (typeof emitStage === 'function') {
          emitStage('retrieve', {
            ...refinedRetrieveStage.retrieveResult.payload,
            contextReferences: typeof summarizeContextReferences === 'function'
              ? summarizeContextReferences(contextRetrieval?.items, 6)
              : [],
            selectedContextItems: typeof summarizeContextItems === 'function'
              ? summarizeContextItems(contextRetrieval?.items, 8)
              : [],
            contextKeywordCoverage: typeof buildKeywordCoverage === 'function'
              ? buildKeywordCoverage({ userText, items: contextRetrieval?.items })
              : null,
            retrievalDecision: refinedRetrieveStage.retrieveResult.decisionCode
          });

          emitStage('sufficiency', {
            retrievalIteration: retrievalIterationCount,
            status: refinedSufficiencyResult?.status || (contextSufficiency.isSufficient ? 'sufficient' : 'insufficient'),
            decisionCode: refinedSufficiencyResult?.decisionCode || contextSufficiency.decisionCode,
            reasonCodes: refinedSufficiencyResult?.reasonCodes || contextSufficiency.reasonCodes,
            ...refinedSufficiencyResult?.payload
          });
        }
      }
    } catch (error) {
      if (typeof emitStage === 'function') {
        emitStage('refine', {
          status: 'failed',
          retrievalIteration: retrievalIterationCount,
          decisionCode: 'refine_error',
          reasonCodes: ['refine_pipeline_error'],
          error: String(error?.message || 'refine retrieval failed')
        });
      }
    }
  } else if (typeof emitStage === 'function') {
    emitStage('refine', {
      status: 'skipped',
      retrievalIteration: retrievalIterationCount,
      decisionCode: !refineEnabled
        ? 'refine_disabled'
        : (maxRefineLoops <= 0 ? 'refine_max_loops_reached' : 'refine_not_required'),
      reasonCodes: !refineEnabled
        ? ['refine_disabled']
        : (maxRefineLoops <= 0 ? ['refine_max_loops_reached'] : ['sufficiency_passed'])
    });
  }

  return {
    queryPlan: nextQueryPlan,
    contextRetrieval,
    contextSufficiency,
    retrievalIterationCount
  };
};
